import { Button } from '@storeblocks/button';
import Modal from '@storeblocks/modal';
import { type FC } from 'react';

import { useOrganizationName } from '@/api/organizations/hooks/useOrganizationName';
import {
  useDeleteOrganizationMutation,
  useOrganizationsQuery,
} from '@/api/organizations/organizationsApi';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { getBasename } from '@/routes/utils/getBasename';
import { TrackingElement, useGetTrackId } from '@/tracking';
import { prettifyOrganizationName } from '@/util/prettifyOrganizationName';

interface Props {
  organizationId: string;
  onClose: () => void;
}

export const DeleteOrganizationModal: FC<Props> = ({
  organizationId,
  onClose,
}) => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const currentOrgCmId = useOrganizationCmId();
  const { organizationName } = useOrganizationName(organizationId);

  const organizationsQuery = useOrganizationsQuery();

  const [deleteOrganization] = useDeleteOrganizationMutation();

  const handleDeleteOrg = (): void => {
    deleteOrganization(organizationId).then(async () => {
      // Redirect to home page if the current organization has been deleted.
      if (currentOrgCmId === organizationId) {
        // Using non-resolving promise to avoid code running when the browser is in the process to redirect.
        await new Promise(() => {
          const redirectUri = `${window.location.origin}${getBasename()}/`;

          window.location.replace(redirectUri);
        });
      } else {
        // Refresh the list of organisations if deleting the organization is successful.
        organizationsQuery.refetch();
      }

      onClose();
    });
  };

  return (
    <Modal
      trigger={undefined}
      open
      onClose={onClose}
      title={`${texts.admin.delete.areYouSure} ${prettifyOrganizationName(
        organizationName,
      )}?`}
      data-test="delete-organization-modal"
      content={texts.admin.delete.cannotBeReverted}
      footer={
        <WithGap gap="16" direction="row">
          <Button
            onClick={handleDeleteOrg}
            key="delete-button"
            data-trackid={getTrackId(
              'delete:confirm-delete-organization-modal',
              TrackingElement.Button,
            )}
            data-test="delete-organization-button"
          >
            {texts.admin.organization.delete}
          </Button>
          <Button
            variant="subtle"
            onClick={onClose}
            key="cancel-button"
            data-trackid={getTrackId(
              'cancel:delete-organization-modal',
              TrackingElement.Button,
            )}
          >
            {texts.admin.cancel}
          </Button>
        </WithGap>
      }
    ></Modal>
  );
};
